// types
import {
  APIStatusItem,
  CanfarServiceType,
  DataItemType,
  FileBrowserPage,
  ToolType,
  Site,
  Theme,
} from './types';

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	when events are raised we set the event target. Global
//	events are propogated to ALL components, and other events
//	are propogated only to the components at which they are
//	targetted.
//
//	------------------------------------------------------------

export enum EventTarget {
  GLOBAL,
  APP,
  HOME,
  PROJECT_FILES,
  DATA_MANAGEMENT_TABLE,
  SEARCH_CATALOG,
  SEARCH_CATALOG_FORM,
  SEARCH_CATALOG_SELECTED_ITEMS_PANEL,
  SEARCH_CATALOG_SELECTED_ITEMS_TAB,
  SEARCH_CATALOG_DATA_COLLECTION_PANEL,
  SEARCH_COMPUTE_TABLE,
  SEARCH_COMPUTE,
  VIEW_NOTIFICATIONS,
  USER_CONTROL,
  USER_CONTROL_DROPDOWN_MENU,
  COMPUTE_JOBS,
} // EventTarget

//	------------------------------------------------------------
//
//	all the possible event types are listed here
//
//	------------------------------------------------------------

export enum EventType {
  DATA_COLLECTION_ADDED,
  DATA_COLLECTION_DELETED,
  DATA_COLLECTION_ALREADY_EXISTS,
  DATA_COLLECTION_DOES_NOT_EXIST,
  USER_NOT_PROJECT_MEMBER_NEW_COLLECTION,
  USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION,
  ITEMS_ADDED_TO_DATA_COLLECTION,
  ITEMS_REMOVED_FROM_DATA_COLLECTION,
  INITIATE_DM_SEARCH,
  INITIATE_COMPUTE_SEARCH,
  REFRESH_COMPUTE_SEARCH,
  INITIATE_DATA_SEARCH,
  UPDATE_SEARCH_RA_DEC,
  LAUNCH_NOTEBOOK, // string (url)
  GET_NOTIFICATIONS,
  DELETE_NOTIFICATIONS,
  MARK_NOTIFICATIONS_READ,
  REFRESH_NOTIFICATIONS,
  MOVE_DATA_TO_STORAGE,
  GET_DATA_MANAGEMENT_JOBS,
  CREATE_NEW_DATA_COLLECTION,
  ADD_TO_EXISTING_DATA_COLLECTION,
  REMOVE_FROM_DATA_COLLECTION,
  DELETE_DATA_COLLECTION,
  DATA_QUERY_ADDED,
  DATA_QUERY_DELETED,
  SET_PREFERENCES,
  LOAD_BACKEND_LOGS,
  FETCH_COMPUTE_SESSIONS,
  EXECUTE_ADQL_QUERY,
  COMPUTE_SERVICE_STATUS_CHANGED,
  API_STATUS_UPDATE,
  COMPUTE_JOB_ADDED,
  COMPUTE_JOB_UPDATED,
  COMPUTE_JOB_DELETED,
  INITIATE_PROJECT_FILES_SEARCH,
  GET_COMPUTE_JOBS,
} // EventType

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	holds details on a current event.
//
//	------------------------------------------------------------

export interface Event {
  eventTarget: EventTarget;
  eventType: EventType;
  parameters?: any;
} // Event

//	------------------------------------------------------------
//
//	raise an event following an attempt to add a collection or
//	add items to a collection.
//
//	------------------------------------------------------------

export interface AddCollectionEvent {
  collectionName?: string;
  projectName?: string;
  itemsAdded?: number;
  messageTarget?: EventTarget;
} // AddCollectionEvent

//	------------------------------------------------------------
//
//	parameters for creating or amending the name of a
//	data collection.
//
//	------------------------------------------------------------

export interface DataCollection {
  dataCollection?: { id: number; name: string };
  project?: { id: number; name?: string };
  dids?: {
    namespace: string;
    filename: string;
  }[];
  collectionIDs?: number[];
  messageSearchCatalog?: boolean;
} // DataCollection

//	------------------------------------------------------------
//
//	a new ADQL query to runs (or just display).
//
//	------------------------------------------------------------

export interface ExecuteADQLQuery {
  adqlQuery: string;
  displayOnly?: boolean;
} // ExecuteADQLQuery

//	------------------------------------------------------------
//
//	holds the user preferences.
//
//	------------------------------------------------------------

export interface GatewaySettingsType {
  theme?: Theme;
  language?: string;
  iconSize?: string;
  fontSize?: number;
  defaultProject?: number;
  defaultSiteUUID?: string;
  popupNotificationsEnabled?: boolean;
  popupLogsEnabled?: boolean;
} // GatewaySettingsType

//	------------------------------------------------------------
//
//	raise an event to initiate a compute search.
//
//	------------------------------------------------------------

export interface InitiateComputeSearchParams {
  node?: string;
  site?: string;
  gpu?: boolean;
  largeScratch?: boolean;
  highMemory?: boolean;
  fastScratch?: boolean;
  description?: string;
  hardwareType?: string;
  middlewareVersion?: string;
  serviceType?: string;
  availabilityType?: string;
  latitude?: number;
  longitude?: number;
  showPage?: boolean;
} // InitiateComputeSearchParams

//	------------------------------------------------------------
//
//	raise an event to initiate a data-management search.
//
//	------------------------------------------------------------

export interface InitiateDMSearchParams {
  namespace?: string;
  filename?: string;
  jobID?: string;
  dataset?: string;
  project?: {
    id: number;
    name?: string;
  };
  collection?: {
    id: number;
    name: string;
  };
  preferredUsername?: string;
  storagePath?: string[];
  canfarService?: CanfarServiceType;
  showHiddenFiles?: boolean;
  fileType?: string;
  pageDisplayed?: FileBrowserPage;
  showPage?: boolean;
  storageType?: string;
  focus?: boolean;
} // InitiateDMSearchParams

//	------------------------------------------------------------
//
//	parameters for launching a data-management job, moving
//	data to a storage location.
//
//	------------------------------------------------------------

export interface MoveDataToStorage {
  toStorageAreaUUID: string;
  toSiteUUID: string;
  toStorageIdentifier: string;
  lifetime: number;
  filesToMove: DataItemType[];
} // MoveDataToStorage

//	------------------------------------------------------------
//
//	a position and fov.
//
//	------------------------------------------------------------

export interface PositionAndFOV {
  position?: {
    ra: number;
    dec: number;
  };
  fov?: number;
} // PositionAndFOV

//	------------------------------------------------------------
//
//	a position that also holds a string identifier of an
//	observation.
//
//	------------------------------------------------------------

export interface PositionWithID {
  position: {
    id: string;
    ra: number;
    dec: number;
  }[];
} // PositionWithID

//	------------------------------------------------------------
//
//	holds the updated status of a compute service.
//
//	------------------------------------------------------------

export interface ServiceStatusParams {
  id: string;
  activeStatus?: boolean;
  healthStatus?: string;
  topBanner?: string;
  bottomBannerType?: string;
  bottomBanner?: string;
} // ServiceStatusParams

//  ------------------------------------------------------------
//
//  Used when raising an event for status updates of external
//  APIs.
//
//  ------------------------------------------------------------

export interface APIStatusParams {
  apiStatuses: APIStatusItem[];
} // APIStatusParams

export interface ToolDescription {
  text: string;
  toolType: ToolType;
  link: string;
  subItems: ToolDescription[];
  needSiteCapabilities?: boolean;
  needDataManagement?: boolean;
  needLoggedIn?: boolean;
  needJupyterURL?: boolean;
}
